import React, { useMemo, useState } from 'react';
import { Search, Save, RotateCcw, Languages, Loader2, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLang } from '../i18n/LanguageContext';
import catalog from '../data/siteTextsCatalog.json';

const PAGE = 40;

const TextField = ({ value, onChange, multiline, dir, testId, placeholder }) => {
  const cls = 'w-full rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-[border-color,box-shadow]';
  return multiline
    ? <textarea data-testid={testId} dir={dir} rows={3} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={`${cls} resize-y leading-relaxed`} />
    : <input data-testid={testId} dir={dir} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={`${cls} h-10`} />;
};

const TextCard = ({ item, override, idx, onSaved, L }) => {
  const current = override || { ar: item.ar, en: item.en };
  const [draft, setDraft] = useState(current);
  const [busy, setBusy] = useState('');
  const dirty = draft.ar !== current.ar || draft.en !== current.en;
  const empty = !draft.ar.trim() || !draft.en.trim();

  const translate = async () => {
    setBusy('tr');
    try {
      const { data } = await api.post('/translate', { text: draft.ar, source_lang: 'ar', target_lang: 'en' });
      setDraft(d => ({ ...d, en: data.text || d.en }));
    } catch (e) {
      toast.error(L('تعذرت الترجمة', 'Translation failed'));
    } finally { setBusy(''); }
  };
  const save = async () => {
    setBusy('save');
    try {
      await api.put('/site-texts', { key: item.key, ar: draft.ar, en: draft.en });
      await onSaved();
      toast.success(L('تم الحفظ', 'Saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('تعذر الحفظ', 'Could not save'));
    } finally { setBusy(''); }
  };
  const restore = async () => {
    setBusy('reset');
    try {
      await api.post('/site-texts/reset', { key: item.key });
      setDraft({ ar: item.ar, en: item.en });
      await onSaved();
      toast.success(L('تم استرجاع النص الأصلي', 'Original text restored'));
    } catch (e) {
      toast.error(L('تعذر الاسترجاع', 'Could not restore'));
    } finally { setBusy(''); }
  };

  return (
    <div data-testid={`site-text-card-${idx}`} className={`bg-white rounded-2xl border p-4 sm:p-5 shadow-sm transition-colors ${dirty ? 'border-amber-300 ring-2 ring-amber-100' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-xs font-semibold text-slate-500">{item.where}</span>
        {override && <span data-testid={`site-text-modified-${idx}`} className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-bold flex items-center gap-1"><PencilLine className="w-3 h-3" />{L('معدّل', 'Edited')}</span>}
        {dirty && <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold">{L('غير محفوظ', 'Unsaved')}</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] font-bold text-slate-500 mb-1">العربية</div>
          <TextField testId={`site-text-ar-${idx}`} dir="rtl" multiline={item.multiline} value={draft.ar} onChange={(v) => setDraft(d => ({ ...d, ar: v }))} />
        </div>
        <div>
          <div className="text-[11px] font-bold text-slate-500 mb-1">English</div>
          <TextField testId={`site-text-en-${idx}`} dir="ltr" multiline={item.multiline} value={draft.en} onChange={(v) => setDraft(d => ({ ...d, en: v }))} />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <button type="button" data-testid={`site-text-save-${idx}`} disabled={!dirty || empty || !!busy} onClick={save} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold transition-colors">
          {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{L('حفظ', 'Save')}
        </button>
        <button type="button" data-testid={`site-text-translate-${idx}`} disabled={!draft.ar.trim() || !!busy} onClick={translate} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold disabled:opacity-50 transition-colors">
          {busy === 'tr' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}{L('ترجمة للإنجليزية', 'Translate to English')}
        </button>
        {override && (
          <button type="button" data-testid={`site-text-reset-${idx}`} disabled={!!busy} onClick={restore} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-red-200 bg-white hover:bg-red-50 text-red-600 text-xs font-bold disabled:opacity-50 transition-colors">
            {busy === 'reset' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}{L('استرجاع النص الأصلي', 'Restore original')}
          </button>
        )}
        {empty && <span className="text-xs text-red-600">{L('لا يمكن حفظ نص فارغ', 'Text cannot be empty')}</span>}
      </div>
    </div>
  );
};

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700'}`}>
    {children}
  </button>
);

const SiteTextsManager = () => {
  const { lang, overrides, reloadSiteTexts } = useLang();
  const L = (ar, en) => (lang === 'ar' ? ar : en);
  const [group, setGroup] = useState('all');
  const [q, setQ] = useState('');
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return catalog.items.filter((it) => {
      if (group !== 'all' && it.group !== group) return false;
      if (modifiedOnly && !overrides[it.key]) return false;
      if (!needle) return true;
      const o = overrides[it.key] || {};
      return [it.ar, it.en, o.ar, o.en, it.where].some(s => (s || '').toLowerCase().includes(needle));
    });
  }, [group, q, modifiedOnly, overrides]);

  const reset = (fn) => (v) => { fn(v); setLimit(PAGE); };
  const modifiedCount = catalog.items.filter(it => overrides[it.key]).length;

  return (
    <div data-testid="site-texts-manager" className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-slate-400" />
            <input data-testid="site-texts-search" value={q} onChange={(e) => reset(setQ)(e.target.value)} placeholder={L('ابحث عن أي نص بالعربية أو الإنجليزية...', 'Search any text in Arabic or English...')} className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 ps-9 pe-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 font-semibold cursor-pointer select-none">
            <input data-testid="site-texts-modified-only" type="checkbox" checked={modifiedOnly} onChange={(e) => reset(setModifiedOnly)(e.target.checked)} className="w-4 h-4 accent-blue-600" />
            {L('المعدّلة فقط', 'Edited only')} <span className="text-slate-400" dir="ltr">({modifiedCount})</span>
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testId="site-texts-group-all" active={group === 'all'} onClick={() => reset(setGroup)('all')}>{L('الكل', 'All')} <span dir="ltr">({catalog.items.length})</span></Chip>
          {Object.entries(catalog.groups).map(([key, label]) => (
            <Chip key={key} testId={`site-texts-group-${key}`} active={group === key} onClick={() => reset(setGroup)(key)}>
              {label} <span dir="ltr">({catalog.items.filter(i => i.group === key).length})</span>
            </Chip>
          ))}
        </div>
      </div>

      <div className="text-sm text-slate-500" data-testid="site-texts-count">{L(`${filtered.length} نص`, `${filtered.length} texts`)}</div>
      <div className="space-y-3">
        {filtered.slice(0, limit).map((it) => (
          <TextCard key={`${it.key}|${overrides[it.key]?.ar || ''}|${overrides[it.key]?.en || ''}`} item={it} idx={catalog.items.indexOf(it)} override={overrides[it.key]} onSaved={reloadSiteTexts} L={L} />
        ))}
        {!filtered.length && <div className="text-center text-slate-500 py-10">{L('لا توجد نصوص مطابقة', 'No matching texts')}</div>}
      </div>
      {filtered.length > limit && (
        <div className="text-center">
          <button type="button" data-testid="site-texts-more" onClick={() => setLimit(l => l + PAGE)} className="h-10 px-6 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-bold transition-colors">
            {L('عرض المزيد', 'Show more')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SiteTextsManager;
